import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CampaGift, GiftAuditEntry, GiftDispute, GiftLedgerAccount, GiftLedgerEvent, GiftLedgerTransaction, GiftTypeConfig, Merchant, Redemption, Settlement } from '../types/campagift';
import { activeMerchantId, campaGifts as seedGifts, campaigns as seedCampaigns, demoCodes, disputes as seedDisputes, featureFlags, giftAudit as seedAudit, giftTypes, merchants, redemptions as seedRedemptions, settlements as seedSettlements, currentHandle } from '../data/campagift';
import { commissionFor, deriveStatus, evaluateEligibility, generateGiftCode, hashCode, remainingKobo, verifyCode } from '../utils/campagift';

/* ------------------------------------------------------------------ *
 * Ledger seed — issuance and redemption history for the seeded state.
 * ------------------------------------------------------------------ */

function tx(id: string, reference: string, event: GiftLedgerEvent, narrative: string, at: string, giftId: string | null, debit: GiftLedgerAccount, credit: GiftLedgerAccount, amountKobo: number, extra: Partial<GiftLedgerTransaction> = {}): GiftLedgerTransaction {
  return {
    id,
    reference,
    event,
    narrative,
    at,
    giftId,
    entries: [{
      account: debit,
      direction: 'debit',
      amountKobo
    }, {
      account: credit,
      direction: 'credit',
      amountKobo
    }],
    ...extra
  };
}
const SEED_LEDGER: GiftLedgerTransaction[] = [tx('gl-1', 'CGL-000101', 'issuance', 'CampaGift General issued to @mary.o', '2026-08-02', 'cg-1001', 'funding:external', 'campagift:outstanding', 1_000_000, {
  provider: 'Licensed payment processor',
  providerReference: 'PSP-77120'
}), tx('gl-2', 'CGL-000102', 'purchase_fee', 'Purchase fee on CG-4H8K2M', '2026-08-02', 'cg-1001', 'funding:external', 'platform:fees', 15_000), tx('gl-3', 'CGL-000108', 'issuance', 'CampaGift Books issued to @mary.o', '2026-07-18', 'cg-1002', 'funding:external', 'campagift:outstanding', 1_500_000, {
  provider: 'Licensed payment processor',
  providerReference: 'PSP-76004'
}), tx('gl-4', 'CGL-000131', 'redemption', 'Redeemed at Campus Books · CGR-2026-000311', '2026-07-24', 'cg-1002', 'campagift:outstanding', 'merchant:payable', 600_000), tx('gl-5', 'CGL-000132', 'commission', 'Merchant commission 4.5% · CGR-2026-000311', '2026-07-24', 'cg-1002', 'merchant:payable', 'platform:commission', 27_000), tx('gl-6', 'CGL-000140', 'issuance', 'Back to School campaign gift issued to @mary.o', '2026-08-05', 'cg-1003', 'campaign:budget', 'campagift:outstanding', 500_000), tx('gl-7', 'CGL-000144', 'issuance', 'CampaGift Event issued to @mary.o', '2026-08-12', 'cg-1004', 'funding:external', 'campagift:outstanding', 700_000, {
  provider: 'Licensed payment processor',
  providerReference: 'PSP-78455'
}), tx('gl-7b', 'CGL-000119', 'issuance', 'CampaGift General issued to @chidi.n', '2026-06-20', 'cg-1005', 'funding:external', 'campagift:outstanding', 600_000, {
  provider: 'Licensed payment processor',
  providerReference: 'PSP-71880'
}), tx('gl-8', 'CGL-000150', 'redemption', 'Redeemed at Akure Print Hub · CGR-2026-000348', '2026-07-30', 'cg-1005', 'campagift:outstanding', 'merchant:payable', 200_000), tx('gl-9', 'CGL-000151', 'commission', 'Merchant commission 5% · CGR-2026-000348', '2026-07-30', 'cg-1005', 'merchant:payable', 'platform:commission', 10_000), tx('gl-10', 'CGL-000155', 'redemption', 'Redeemed at Campus Books · CGR-2026-000352', '2026-08-01', 'cg-1005', 'campagift:outstanding', 'merchant:payable', 400_000), tx('gl-11', 'CGL-000156', 'commission', 'Merchant commission 4.5% · CGR-2026-000352', '2026-08-01', 'cg-1005', 'merchant:payable', 'platform:commission', 18_000), tx('gl-12', 'CGL-000158', 'redemption', 'Duplicate scan · CGR-2026-000355', '2026-08-01', 'cg-1005', 'campagift:outstanding', 'merchant:payable', 100_000), tx('gl-13', 'CGL-000161', 'reversal', 'Reversal of CGR-2026-000355 — compensating entry', '2026-08-01', 'cg-1005', 'merchant:payable', 'campagift:outstanding', 100_000, {
  correctsReference: 'CGL-000158'
}), tx('gl-14', 'CGL-000170', 'settlement', 'Settlement CGS-2026-W30-CB paid to Campus Books', '2026-08-05', null, 'merchant:payable', 'settlement:bank', 955_000, {
  provider: 'Licensed payout provider',
  providerReference: 'PP-88213-A'
})];

/* ------------------------------------------------------------------ */

export interface RedeemInput {
  giftId: string;
  merchantId: string;
  amountKobo: number;
  actor: string;
  idempotencyKey: string;
  purchaseCategoryId?: string;
  orderReference?: string;
  otherPaymentKobo?: number;
}
export type RedeemResult = {
  ok: true;
  redemption: Redemption;
} | {
  ok: false;
  error: string;
  checks?: {
    rule: string;
    passed: boolean;
    detail: string;
  }[];
};
interface IssueInput {
  typeId: string;
  recipientHandle: string;
  faceValueKobo: number;
  message: string;
  merchantId?: string;
  paymentConfirmed: boolean;
}
interface CampaGiftValue {
  gifts: CampaGift[];
  redemptions: Redemption[];
  ledger: GiftLedgerTransaction[];
  settlements: Settlement[];
  disputes: GiftDispute[];
  audit: GiftAuditEntry[];
  merchants: Merchant[];
  types: GiftTypeConfig[];
  campaigns: typeof seedCampaigns;
  flags: typeof featureFlags;
  handle: string;
  merchantId: string;
  /** Codes revealed in this session only. Never persisted in the clear. */
  revealedCodes: Record<string, string>;
  failedCodeAttempts: number;
  codeLockedUntil: number | null;
  typeOf: (gift: CampaGift) => GiftTypeConfig;
  merchantOf: (id: string) => Merchant | undefined;
  remaining: (gift: CampaGift) => number;
  status: (gift: CampaGift) => ReturnType<typeof deriveStatus>;
  issue: (input: IssueInput) => {
    ok: true;
    gift: CampaGift;
    code: string;
  } | {
    ok: false;
    error: string;
  };
  accept: (giftId: string) => void;
  decline: (giftId: string) => void;
  transfer: (giftId: string, toHandle: string, reason: string) => {
    ok: boolean;
    error?: string;
  };
  revealCode: (giftId: string) => void;
  lookupByCode: (code: string) => {
    ok: true;
    gift: CampaGift;
  } | {
    ok: false;
    error: string;
  };
  redeem: (input: RedeemInput) => RedeemResult;
  reverse: (reference: string, reason: string, actor: string) => {
    ok: boolean;
    error?: string;
  };
  freeze: (giftId: string, reason: string, actor: string) => void;
  unfreeze: (giftId: string, reason: string, actor: string) => void;
  raiseDispute: (giftId: string, kind: string, detail: string) => void;
  advanceSettlement: (settlementId: string, actor: string) => void;
  openSettlement: (merchantId: string, actor: string) => {
    ok: boolean;
    error?: string;
  };
  eligibility: (giftId: string, merchantId: string, amountKobo?: number, categoryId?: string) => ReturnType<typeof evaluateEligibility> | null;
}
const CampaGiftContext = createContext<CampaGiftValue | null>(null);
let sequence = 400;
function nextRef(prefix: string): string {
  sequence += 1;
  return `${prefix}-2026-${String(sequence).padStart(6, '0')}`;
}
export function CampaGiftProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [gifts, setGifts] = useState<CampaGift[]>(seedGifts);
  const [redemptions, setRedemptions] = useState<Redemption[]>(seedRedemptions);
  const [ledger, setLedger] = useState<GiftLedgerTransaction[]>(SEED_LEDGER);
  const [settlements, setSettlements] = useState<Settlement[]>(seedSettlements);
  const [disputes, setDisputes] = useState<GiftDispute[]>(seedDisputes);
  const [audit, setAudit] = useState<GiftAuditEntry[]>(seedAudit);
  const [revealedCodes, setRevealedCodes] = useState<Record<string, string>>({});
  const [failedCodeAttempts, setFailedCodeAttempts] = useState(0);
  const [codeLockedUntil, setCodeLockedUntil] = useState<number | null>(null);
  const now = () => new Date().toISOString();
  const log = useCallback((actor: string, action: string, entity: string, from: string | null, to: string | null, reason: string | null) => {
    setAudit(current => [{
      id: `ga-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      at: new Date().toISOString().slice(0, 16).replace('T', ' '),
      actor,
      action,
      entity,
      from,
      to,
      reason
    }, ...current]);
  }, []);
  const write = useCallback((entries: GiftLedgerTransaction[]) => {
    setLedger(current => [...entries, ...current]);
  }, []);
  const typeOf = useCallback((gift: CampaGift) => giftTypes.find(t => t.id === gift.typeId) ?? giftTypes[0], []);
  const merchantOf = useCallback((id: string) => merchants.find(m => m.id === id), []);
  const remaining = useCallback((gift: CampaGift) => remainingKobo(gift, redemptions), [redemptions]);
  const status = useCallback((gift: CampaGift) => deriveStatus(gift, redemptions), [redemptions]);

  /* ---------------- ISSUANCE ---------------- */

  const issue = useCallback<CampaGiftValue['issue']>(({
    typeId,
    recipientHandle,
    faceValueKobo,
    message,
    merchantId,
    paymentConfirmed
  }) => {
    const type = giftTypes.find(t => t.id === typeId);
    if (!type) return {
      ok: false,
      error: 'Unknown gift type.'
    };
    if (!type.enabled) return {
      ok: false,
      error: `${type.name} is not currently enabled.`
    };
    if (faceValueKobo < type.minValueKobo || faceValueKobo > type.maxValueKobo) {
      return {
        ok: false,
        error: 'Value is outside the limits configured for this gift type.'
      };
    }
    const clean = recipientHandle.replace(/^@/, '').trim();
    if (!clean) return {
      ok: false,
      error: 'Choose a recipient by Campuzon handle.'
    };
    if (clean === currentHandle) {
      return {
        ok: false,
        error: 'You cannot gift to your own account. Self-gifting is refused and recorded.'
      };
    }
    /**
     * Issuance NEVER happens because a button was pressed. It happens only on
     * authoritative payment confirmation (or an authorised non-payment
     * issuance route such as a campaign budget).
     */
    if (!paymentConfirmed && type.funding === 'user_purchase') {
      return {
        ok: false,
        error: 'Awaiting payment confirmation from the provider. No value has been issued.'
      };
    }
    const code = generateGiftCode();
    const id = `cg-${Date.now()}`;
    const purchaseFee = Math.round(faceValueKobo * type.purchaseFeeBps / 10_000);
    const expiresAt = type.expiryDays ? new Date(Date.now() + type.expiryDays * 86_400_000).toISOString().slice(0, 10) : null;
    const gift: CampaGift = {
      id,
      publicReference: `CG-${code.replace(/-/g, '').slice(0, 6)}`,
      instrumentId: `CGI-${String(Date.now()).slice(-9)}`,
      typeId,
      issuer: {
        id: 'usr-self',
        kind: 'user',
        name: `@${currentHandle}`,
        authorised: true
      },
      purchaserHandle: currentHandle,
      holderHandle: clean,
      faceValueKobo,
      currency: 'NGN',
      issuedAt: new Date().toISOString().slice(0, 10),
      expiresAt,
      status: type.acceptanceRequired ? 'issued' : 'available',
      message: message.trim() || null,
      campaignId: null,
      riskState: 'clear',
      acceptance: type.acceptanceRequired ? 'pending' : 'auto',
      transfers: [],
      codeHash: hashCode(code),
      codeLast4: code.slice(-4)
    };
    const fundingAccount: GiftLedgerAccount = type.funding === 'campaign_budget' ? 'campaign:budget' : type.funding === 'merchant_promotion' ? 'promotion:merchant' : 'funding:external';
    const entries = [tx(`gl-${id}-1`, nextRef('CGL'), 'issuance', `${type.name} issued to @${clean}`, now(), id, fundingAccount, 'campagift:outstanding', faceValueKobo, {
      provider: type.funding === 'user_purchase' ? 'Licensed payment processor' : undefined,
      providerReference: type.funding === 'user_purchase' ? `PSP-${Date.now().toString().slice(-5)}` : null
    })];
    if (purchaseFee > 0) {
      entries.push(tx(`gl-${id}-2`, nextRef('CGL'), 'purchase_fee', `Purchase fee on ${gift.publicReference}`, now(), id, fundingAccount, 'platform:fees', purchaseFee));
    }
    setGifts(current => [gift, ...current]);
    write(entries);
    setRevealedCodes(current => ({
      ...current,
      [id]: code
    }));
    log(`@${currentHandle}`, 'Gift issued', gift.publicReference, null, 'available', `${type.name} to @${clean}`);
    return {
      ok: true,
      gift,
      code
    };
  }, [log, write]);

  /* ---------------- ACCEPTANCE AND TRANSFER ---------------- */

  const accept = useCallback((giftId: string) => {
    setGifts(current => current.map(g => g.id === giftId ? {
      ...g,
      acceptance: 'accepted',
      status: 'available'
    } : g));
    log(`@${currentHandle}`, 'Gift accepted', giftId, 'pending', 'accepted', null);
  }, [log]);
  const decline = useCallback((giftId: string) => {
    setGifts(current => current.map(g => g.id === giftId ? {
      ...g,
      acceptance: 'declined',
      status: 'closed'
    } : g));
    log(`@${currentHandle}`, 'Gift declined', giftId, 'pending', 'declined', null);
  }, [log]);
  const transfer = useCallback<CampaGiftValue['transfer']>((giftId, toHandle, reason) => {
    const gift = gifts.find(g => g.id === giftId);
    if (!gift) return {
      ok: false,
      error: 'Gift not found.'
    };
    const type = typeOf(gift);
    if (type.transferability !== 'transferable') {
      return {
        ok: false,
        error: `${type.name} is ${type.transferability.replace(/_/g, '-')}. It cannot be transferred.`
      };
    }
    const clean = toHandle.replace(/^@/, '').trim();
    if (!clean) return {
      ok: false,
      error: 'Enter the recipient handle.'
    };
    if (clean === gift.holderHandle) return {
      ok: false,
      error: 'That is already the holder.'
    };
    const value = remainingKobo(gift, redemptions);
    if (value <= 0) return {
      ok: false,
      error: 'No remaining value to transfer.'
    };
    setGifts(current => current.map(g => g.id === giftId ? {
      ...g,
      holderHandle: clean,
      transfers: [...g.transfers, {
        id: `tr-${Date.now()}`,
        fromHandle: g.holderHandle,
        toHandle: clean,
        at: new Date().toISOString().slice(0, 10),
        reason: reason || 'Holder transfer'
      }]
    } : g));
    write([tx(`gl-tr-${Date.now()}`, nextRef('CGL'), 'transfer', `${gift.publicReference} transferred to @${clean}`, now(), gift.id, 'campagift:outstanding', 'campagift:outstanding', value)]);
    log(`@${gift.holderHandle}`, 'Gift transferred', gift.publicReference, `@${gift.holderHandle}`, `@${clean}`, reason || null);
    return {
      ok: true
    };
  }, [gifts, redemptions, typeOf, write, log]);
  const revealCode = useCallback((giftId: string) => {
    /* The stored hash cannot produce the code. A real reveal re-issues a
       short-lived redemption token from the service. */
    const gift = gifts.find(g => g.id === giftId);
    if (!gift) return;
    setRevealedCodes(current => ({
      ...current,
      [giftId]: demoCodes[giftId] ?? 'Token issued by the service'
    }));
    log(`@${currentHandle}`, 'Redemption token revealed', gift.publicReference, null, null, 'Shown to holder on request');
  }, [gifts, log]);

  /* ---------------- CODE LOOKUP (rate limited) ---------------- */

  const lookupByCode = useCallback<CampaGiftValue['lookupByCode']>(code => {
    if (codeLockedUntil && Date.now() < codeLockedUntil) {
      const seconds = Math.ceil((codeLockedUntil - Date.now()) / 1000);
      return {
        ok: false,
        error: `Too many failed attempts. Try again in ${seconds}s.`
      };
    }
    const normalised = code.trim().toUpperCase();
    const match = gifts.find(g => verifyCode(normalised, g.codeHash));
    if (!match) {
      const attempts = failedCodeAttempts + 1;
      setFailedCodeAttempts(attempts);
      if (attempts >= 5) {
        setCodeLockedUntil(Date.now() + 60_000);
        setFailedCodeAttempts(0);
        return {
          ok: false,
          error: 'Too many failed attempts. Code entry is locked for 60 seconds and the pattern has been flagged.'
        };
      }
      return {
        ok: false,
        error: `No gift matches that code. ${5 - attempts} attempt${5 - attempts === 1 ? '' : 's'} before code entry is locked.`
      };
    }
    setFailedCodeAttempts(0);
    return {
      ok: true,
      gift: match
    };
  }, [gifts, failedCodeAttempts, codeLockedUntil]);

  /* ---------------- ELIGIBILITY ---------------- */

  const eligibility = useCallback<CampaGiftValue['eligibility']>((giftId, merchantId, amountKobo, categoryId) => {
    const gift = gifts.find(g => g.id === giftId);
    const merchant = merchants.find(m => m.id === merchantId);
    if (!gift || !merchant) return null;
    return evaluateEligibility({
      gift,
      type: typeOf(gift),
      merchant,
      redemptions,
      requestedKobo: amountKobo,
      purchaseCategoryId: categoryId
    });
  }, [gifts, redemptions, typeOf]);

  /* ---------------- REDEMPTION ---------------- */

  const redeem = useCallback<CampaGiftValue['redeem']>(({
    giftId,
    merchantId,
    amountKobo,
    actor,
    idempotencyKey,
    purchaseCategoryId,
    orderReference,
    otherPaymentKobo
  }) => {
    /* Idempotency: the same key can never spend value twice. */
    const existing = redemptions.find(r => r.idempotencyKey === idempotencyKey);
    if (existing) return {
      ok: true,
      redemption: existing
    };
    const gift = gifts.find(g => g.id === giftId);
    const merchant = merchants.find(m => m.id === merchantId);
    if (!gift || !merchant) return {
      ok: false,
      error: 'Gift or merchant not found.'
    };
    const type = typeOf(gift);
    const result = evaluateEligibility({
      gift,
      type,
      merchant,
      redemptions,
      requestedKobo: amountKobo,
      purchaseCategoryId
    });
    if (!result.eligible) {
      const failed = result.checks.find(c => !c.passed);
      return {
        ok: false,
        error: failed?.detail ?? 'Redemption refused.',
        checks: result.checks
      };
    }

    /* Double-spend guard: re-read remaining value at commit time. */
    const value = remainingKobo(gift, redemptions);
    if (amountKobo > value) {
      return {
        ok: false,
        error: 'Remaining value changed. Re-check the gift and try again.'
      };
    }
    const {
      commissionKobo,
      netKobo,
      bps
    } = commissionFor(amountKobo, type, merchant);
    const reference = nextRef('CGR');
    const redemption: Redemption = {
      id: `rd-${Date.now()}`,
      reference,
      giftId,
      merchantId,
      amountKobo,
      commissionKobo,
      netReceivableKobo: netKobo,
      at: now(),
      state: 'confirmed',
      actor,
      settlementId: null,
      idempotencyKey,
      orderReference,
      otherPaymentKobo
    };
    setRedemptions(current => [redemption, ...current]);
    write([tx(`gl-rd-${redemption.id}`, nextRef('CGL'), 'redemption', `Redeemed at ${merchant.name} · ${reference}`, now(), giftId, 'campagift:outstanding', 'merchant:payable', amountKobo), ...(commissionKobo > 0 ? [tx(`gl-cm-${redemption.id}`, nextRef('CGL'), 'commission', `Merchant commission ${(bps / 100).toFixed(2)}% · ${reference}`, now(), giftId, 'merchant:payable', 'platform:commission', commissionKobo)] : [])]);
    log(actor, 'Redemption confirmed', reference, null, 'confirmed', `${merchant.name} · ${gift.publicReference}`);
    return {
      ok: true,
      redemption
    };
  }, [gifts, redemptions, typeOf, write, log]);
  const reverse = useCallback<CampaGiftValue['reverse']>((reference, reason, actor) => {
    const original = redemptions.find(r => r.reference === reference);
    if (!original) return {
      ok: false,
      error: 'Redemption not found.'
    };
    if (original.state === 'reversed') return {
      ok: false,
      error: 'Already reversed.'
    };
    if (!reason.trim()) return {
      ok: false,
      error: 'A reason is required to reverse a redemption.'
    };
    const gift = gifts.find(g => g.id === original.giftId);
    const compensating: Redemption = {
      id: `rd-rev-${Date.now()}`,
      reference: nextRef('CGR'),
      giftId: original.giftId,
      merchantId: original.merchantId,
      amountKobo: -original.amountKobo,
      commissionKobo: -original.commissionKobo,
      netReceivableKobo: -original.netReceivableKobo,
      at: now(),
      state: 'confirmed',
      actor,
      settlementId: null,
      idempotencyKey: `idem-rev-${original.idempotencyKey}`,
      reversalOfReference: original.reference,
      reason
    };

    /* The original is MARKED, never deleted, and its amounts are untouched. */
    setRedemptions(current => [compensating, ...current.map(r => r.reference === reference ? {
      ...r,
      state: 'reversed' as const,
      reason
    } : r)]);
    write([tx(`gl-rev-${compensating.id}`, nextRef('CGL'), 'reversal', `Reversal of ${reference} — compensating entry`, now(), original.giftId, 'merchant:payable', 'campagift:outstanding', original.amountKobo, {
      correctsReference: reference
    })]);
    log(actor, 'Redemption reversed', reference, 'confirmed', 'reversed', reason);
    if (gift) {
      setDisputes(current => current);
    }
    return {
      ok: true
    };
  }, [redemptions, gifts, write, log]);
  const freeze = useCallback((giftId: string, reason: string, actor: string) => {
    setGifts(current => current.map(g => g.id === giftId ? {
      ...g,
      status: 'frozen',
      riskState: 'frozen',
      frozenReason: reason
    } : g));
    log(actor, 'Gift frozen', giftId, 'available', 'frozen', reason);
  }, [log]);
  const unfreeze = useCallback((giftId: string, reason: string, actor: string) => {
    setGifts(current => current.map(g => g.id === giftId ? {
      ...g,
      status: 'available',
      riskState: 'clear',
      frozenReason: undefined
    } : g));
    log(actor, 'Gift unfrozen', giftId, 'frozen', 'available', reason);
  }, [log]);
  const raiseDispute = useCallback((giftId: string, kind: string, detail: string) => {
    const reference = nextRef('CGD');
    setDisputes(current => [{
      id: `dsp-${Date.now()}`,
      reference,
      giftId,
      raisedBy: `@${currentHandle}`,
      raisedByRole: 'user',
      kind,
      detail,
      evidence: [],
      state: 'open',
      reviewer: null,
      decision: null,
      at: new Date().toISOString().slice(0, 10)
    }, ...current]);
    log(`@${currentHandle}`, 'Dispute raised', reference, null, 'open', kind);
  }, [log]);

  /* ---------------- SETTLEMENT ---------------- */

  const openSettlement = useCallback<CampaGiftValue['openSettlement']>((merchantId, actor) => {
    const unsettled = redemptions.filter(r => r.merchantId === merchantId && r.state === 'confirmed' && !r.settlementId);
    if (unsettled.length === 0) return {
      ok: false,
      error: 'No unsettled redemptions for this merchant.'
    };
    const gross = unsettled.reduce((t, r) => t + r.amountKobo, 0);
    const commission = unsettled.reduce((t, r) => t + r.commissionKobo, 0);
    const id = `stl-${Date.now()}`;
    const reference = nextRef('CGS');
    setSettlements(current => [{
      id,
      reference,
      merchantId,
      redemptionReferences: unsettled.map(r => r.reference),
      grossKobo: gross,
      commissionKobo: commission,
      adjustmentsKobo: 0,
      netKobo: gross - commission,
      state: 'eligible',
      openedAt: new Date().toISOString().slice(0, 10),
      settledAt: null,
      provider: 'Licensed payout provider',
      providerReference: null
    }, ...current]);
    setRedemptions(current => current.map(r => unsettled.some(u => u.reference === r.reference) ? {
      ...r,
      settlementId: id
    } : r));
    log(actor, 'Settlement opened', reference, null, 'eligible', `${unsettled.length} redemptions`);
    return {
      ok: true
    };
  }, [redemptions, log]);
  const advanceSettlement = useCallback((settlementId: string, actor: string) => {
    setSettlements(current => current.map(s => {
      if (s.id !== settlementId) return s;
      const order: Settlement['state'][] = ['pending', 'eligible', 'processing', 'submitted', 'settled'];
      const index = order.indexOf(s.state);
      if (index === -1 || index === order.length - 1) return s;
      const next = order[index + 1];
      log(actor, 'Settlement state changed', s.reference, s.state, next, 'Settlement run');
      if (next === 'settled') {
        write([tx(`gl-stl-${s.id}`, nextRef('CGL'), 'settlement', `Settlement ${s.reference} paid`, now(), null, 'merchant:payable', 'settlement:bank', s.netKobo, {
          provider: s.provider,
          providerReference: `PP-${Date.now().toString().slice(-5)}`
        })]);
        return {
          ...s,
          state: next,
          settledAt: new Date().toISOString().slice(0, 10),
          providerReference: `PP-${Date.now().toString().slice(-5)}`
        };
      }
      return {
        ...s,
        state: next
      };
    }));
  }, [log, write]);
  const value = useMemo<CampaGiftValue>(() => ({
    gifts,
    redemptions,
    ledger,
    settlements,
    disputes,
    audit,
    merchants,
    types: giftTypes,
    campaigns: seedCampaigns,
    flags: featureFlags,
    handle: currentHandle,
    merchantId: activeMerchantId,
    revealedCodes,
    failedCodeAttempts,
    codeLockedUntil,
    typeOf,
    merchantOf,
    remaining,
    status,
    issue,
    accept,
    decline,
    transfer,
    revealCode,
    lookupByCode,
    redeem,
    reverse,
    freeze,
    unfreeze,
    raiseDispute,
    advanceSettlement,
    openSettlement,
    eligibility
  }), [gifts, redemptions, ledger, settlements, disputes, audit, revealedCodes, failedCodeAttempts, codeLockedUntil, typeOf, merchantOf, remaining, status, issue, accept, decline, transfer, revealCode, lookupByCode, redeem, reverse, freeze, unfreeze, raiseDispute, advanceSettlement, openSettlement, eligibility]);
  return <CampaGiftContext.Provider value={value} data-id="element-5385" data-mp="31iq0g7nxb8jt">{children}</CampaGiftContext.Provider>;
}
export function useCampaGift(): CampaGiftValue {
  const context = useContext(CampaGiftContext);
  if (!context) throw new Error('useCampaGift must be used inside CampaGiftProvider');
  return context;
}